import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { assets } from "../lib/assets";

const navLinks = [
  { id: "about", enLabel: "About", zhLabel: "关于我们" },
  { id: "products", enLabel: "Products", zhLabel: "产品中心" },
  { id: "quality", enLabel: "Quality", zhLabel: "质量保证" },
  { id: "partners", enLabel: "Why Shadong Prit", zhLabel: "选择普瑞特" },
  { id: "contact", enLabel: "Contact", zhLabel: "联系我们" }
];

export default function Navbar({ lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = navLinks.map(l => document.getElementById(l.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top < 200) {
          setActiveSection(navLinks[i].id);
          return;
        }
      }
      setActiveSection("");
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass shadow-sm py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <button
            data-testid="logo-button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5 group"
          >
            <img
              src={assets.logo}
              alt="Shandong Prit Biotechnology"
              className={`h-14 sm:h-16 w-auto max-w-[200px] sm:max-w-[240px] object-contain object-left ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </button>

          <div className={`hidden lg:flex items-center gap-0.5 px-1.5 py-1 rounded-xl transition-all duration-500 ${
            scrolled ? "" : "bg-emerald-900/50 border border-emerald-400/30 backdrop-blur-md"
          }`}>
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className={`relative px-4 py-2.5 text-base font-medium rounded-lg transition-all duration-300 ${
                  scrolled
                    ? activeSection === link.id
                      ? "text-brand-blue bg-card-mint"
                      : "text-slate-500 hover:text-brand-blue hover:bg-card-mint/70"
                    : activeSection === link.id
                      ? "text-emerald-50 bg-emerald-500/25"
                      : "text-emerald-50/90 hover:text-emerald-50 hover:bg-emerald-500/20"
                }`}
              >
                {lang === "en" ? link.enLabel : link.zhLabel}
                {activeSection === link.id && (
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full ${scrolled ? "bg-brand-blue" : "bg-emerald-400"}`} />
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="language-toggle"
              variant="outline"
              size="sm"
              onClick={() => setLang(lang === "en" ? "zh" : "en")}
              className={`gap-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105 ${
                scrolled
                  ? "border-brand-blue/40 text-brand-blue hover:bg-card-mint hover:border-brand-blue"
                  : "border-emerald-400/40 text-emerald-50 hover:bg-emerald-500/20 bg-emerald-900/30"
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "中文" : "EN"}
            </Button>

            <div className="lg:hidden">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button
                    data-testid="mobile-menu-toggle"
                    variant="ghost"
                    size="icon"
                    className={`rounded-lg ${scrolled ? "text-brand-blue hover:bg-card-mint" : "text-emerald-50 hover:bg-emerald-50/10"}`}
                  >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-card backdrop-blur-xl p-0 border-l border-emerald-200" aria-describedby={undefined}>
                  <span className="sr-only">Navigation Menu</span>
                  <div className="flex flex-col pt-14 px-6">
                    <div className="flex items-center gap-2.5 mb-10">
                      <img src={assets.logo} alt="Shandong Prit Biotechnology" className="h-14 w-auto max-w-[200px] object-contain" />
                    </div>
                    {navLinks.map((link, i) => (
                      <button
                        key={link.id}
                        data-testid={`mobile-nav-${link.id}`}
                        onClick={() => scrollTo(link.id)}
                        className="text-left px-4 py-4 text-foreground hover:text-brand-blue hover:bg-card-mint rounded-lg transition-all duration-200 text-base font-medium border-b border-emerald-100 last:border-0"
                        style={{ animationDelay: `${i * 0.05}s` }}
                      >
                        {lang === "en" ? link.enLabel : link.zhLabel}
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
